import React from 'react';
import classNames from 'classnames';

import I18n from 'i18n-js';

import SanitizedRender from '../../components/sanitized_render';
import CoreLink from '../../components/core_link';
import { MParticleEventName } from '../../elog/mparticle_types';
import { core_apimessages_ContentSponsorship_SponsorshipType } from '../../gql/graphql';
import AdTag from '../../components/ads/ad_tag';
import { useId } from '@reach/auto-id';

export const BUYING_GUIDE = 'buying_guide';
export const BEST_SELLERS = 'best_sellers';

const COMPONENT_NAME = 'MarketingCallout';

interface IProps {
  channel?: string;
  imageURL?: string;
  pageType?: string;
  subtitle?: string;
  targetHref?: string;
  title?: string;
  wideCard?: boolean;
  squareImage?: boolean;
  position?: number;
  trackingComponentName?: string;
  smallTile?: boolean;
  sponsoredContentType?: core_apimessages_ContentSponsorship_SponsorshipType;
  sponsoredContentPartner?: string;
}

export default function MarketingCalloutCard(props: IProps) {
  const id = useId();

  function hasTag() {
    return (
      props.pageType === BUYING_GUIDE ||
      props.pageType === BEST_SELLERS
    );
  }

  function getBackgroundImageStyle() {
    if (!props.imageURL) return null;

    return { backgroundImage: `url(${props.imageURL})` };
  }

  function renderTag() {
    const p = props.pageType;

    return (
      <p className="marketing-callout__tag">
        {p === BUYING_GUIDE && I18n.t('cms.marketingCallout.buyingGuideSubtitle')}
        {p === BEST_SELLERS && I18n.t('cms.marketingCallout.bestSellers')}
      </p>
    );
  }

  function renderSubtitle() {
    if (!props.subtitle) return null;

    return (
      <SanitizedRender
        htmlTag="h4"
        className="marketing-callout__subtitle"
        html={props.subtitle}
        inlineTagsOnly
        blocklistedTags={['a']}
      />
    );
  }

  function renderPartnershipSponsorshipContent(sponsorshipType, partnershipName) {
    if (
      sponsorshipType === core_apimessages_ContentSponsorship_SponsorshipType.PARTNERSHIP &&
        partnershipName) {
      return (
        <p className="size-80 mb-2 style-italic">
          {I18n.t('commons.contentSponsorship.partnership', { partner: partnershipName })}
        </p>
      );
    }

    return null;
  }

  function renderAdSponsorshipContent(sponsorshipType) {
    if (sponsorshipType === core_apimessages_ContentSponsorship_SponsorshipType.AD) {
      return (
        <div className="marketing-callout__ad">
          <AdTag inline small />
        </div>
      );
    }

    return null;
  }

  const classes = classNames(
    'marketing-callout',
    { 'marketing-callout--with-tag': hasTag() },
    { 'marketing-callout--wide': props.wideCard },
    { 'marketing-callout--square': props.squareImage },
    { 'marketing-callout--core': props.channel === 'core' },
    { 'marketing-callout--small': props.smallTile },
  );

  if (!props.targetHref) return null;

  const clickEvent = {
    eventName: MParticleEventName.ClickedMarketingCallout,
    componentName: props.trackingComponentName || COMPONENT_NAME,
    entityId: props.targetHref,
    position: props.position,
  };

  return (
    <CoreLink
      className={classes}
      to={props.targetHref}
      clickEvent={clickEvent}
      aria-labelledby={`marketing-callout__content-${id}`}
      // square image has tweed orange branding; don't flip it in dark mode
      data-theme={props.squareImage && 'light'}
    >
      <div className="marketing-callout__inner">
        <div className="marketing-callout__image-area">
          <div
            className="marketing-callout__image"
            style={getBackgroundImageStyle()}
          >
            {renderAdSponsorshipContent(props.sponsoredContentType)}
          </div>
        </div>
        <div
          className="marketing-callout__text-area"
          id={`marketing-callout__content-${id}`}
        >
          <div className="marketing-callout__text">
            {hasTag() ? renderTag() : renderSubtitle()}
            <SanitizedRender
              htmlTag="h4"
              className="marketing-callout__title"
              html={props.title}
              inlineTagsOnly
              blocklistedTags={['a']}
            />
          </div>
          {renderPartnershipSponsorshipContent(props.sponsoredContentType, props.sponsoredContentPartner)}
        </div>
      </div>
    </CoreLink>
  );
}
